import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import deepmerge from 'deepmerge';
import { ensureArray } from '../common/utils/arrayUtilities';
import { deepClone } from "../common/utils/objectUtilities";

const processJob = (job) => {
    job = deepClone(job)
    if (typeof job.scheduleid === 'string' && !isNaN(parseInt(job.scheduleid))) {
        job.scheduleid = parseInt(job.scheduleid);
    }
    if (typeof job.priority === 'string' && !isNaN(parseInt(job.priority))) {
        job.priority = parseInt(job.priority);
    }
    return job;
};

const scheduledJobSlice = createSlice({
    name: 'scheduledJob',
    initialState: {
        data: [],
        activeJob: undefined,
    },
    reducers: {
        setActiveJob: (state, action) => {
            state.activeJob = action.payload
        },
        set: (state, action) => {
            state.data = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const job of ensureArray(action.payload).map(j => processJob(j))) {
                let idx = state.data.findIndex(j => j.scheduleid.toString() === job.scheduleid.toString());
                if (idx >= 0) {
                    state.data.splice(idx, 1, deepmerge(state.data[idx], job, { arrayMerge: (dst, src, options) => src }));
                } else {
                    state.data.push(job);
                }
            }
        },
        remove: (state, action) => {
            for (const job of ensureArray(action.payload).map(j => processJob(j))) {
                if (job.scheduleid) {
                    let idx = state.data.findIndex(j => j.scheduleid.toString() === job.scheduleid.toString());
                    if (idx >= 0) {
                        state.data.splice(idx, 1);
                    }
                }
                if (state.activeJob?.toString() === job.scheduleid.toString()) {
                    state.activeJob = null;
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => scheduledJobSlice.getInitialState());
    }
});

// #region network operations
const fetchJobs = network => network.get('/api/v1/scheduledJobs', scheduledJobSlice.actions.set);
const fetchJob = scheduleid => {
    return network => network.get('/api/v1/scheduledJob/' + encodeURIComponent(scheduleid), scheduledJobSlice.actions.addOrUpdate);
}
export const createScheduledJob = (network, job) => network.post('/api/v1/scheduledJob', job, scheduledJobSlice.actions.addOrUpdate);
export const updateScheduledJob = (network, job) => {
    if (job.scheduleid) {
        network.put('/api/v1/scheduledJob', job, scheduledJobSlice.actions.addOrUpdate);
    } else {
        network.dispatch(scheduledJobSlice.actions.addOrUpdate(job))
    }
};
export const removeScheduledJob = (network, job) => network.delete('/api/v1/scheduledJob/' + encodeURIComponent(job.scheduleid), scheduledJobSlice.actions.remove);
// #endregion

// #region selectors
const selectAllJobs = state => state.scheduledJob.data;
export const selectActiveScheduledJob = state => state.scheduledJob.activeJob;
export const setActiveScheduledJob = (dispatch, scheduleid) => {
    dispatch(scheduledJobSlice.actions.setActiveJob(scheduleid))
};

const selectJobByID = createSelector([selectAllJobs, (jobs, scheduleid) => scheduleid], (jobs, scheduleid) => jobs.find(j => j.scheduleid.toString() === scheduleid?.toString()));
const selectJob = scheduleid => state => selectJobByID(state, scheduleid);
// #endregion

// #region listeners
const listen = {
    scheduledJobCreated: (job, { dispatch }) => dispatch(scheduledJobSlice.actions.addOrUpdate(job)),
    scheduledJobUpdated: (job, { dispatch }) => dispatch(scheduledJobSlice.actions.addOrUpdate(job)),
    scheduledJobRemoved: (job, { dispatch }) => dispatch(scheduledJobSlice.actions.remove(job))
}
// #endregion

// #region permission checks
export const canCreateScheduledJob = (credentials, job) => credentials.access.hasFull(job);
export const canUpdateScheduledJob = (credentials, job) => credentials.access.hasFull(job);
export const canRemoveScheduledJob = (credentials, job) => credentials.access.hasFull(job);
// #endregion

export const [useScheduledJobs] = dataHookFactory(selectAllJobs, { run: fetchJobs, listen });
export const [useScheduledJob] = dataHookFactory(selectJob, initMapFactory(fetchJob, listen));

const reducerDetails = { name: scheduledJobSlice.name, reducer: scheduledJobSlice.reducer };
export default reducerDetails;
